import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import ImageCropModal from '../ImageCropModal';
import {
  X, Check, Plus, Globe, Lock, Link as LinkIcon, Loader2,
  Camera, ImageIcon
} from 'lucide-react';
import { API, resolveUrl } from './shared';

const EditCommunityModal = ({ community, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: community.name || '',
    description: community.description || '',
    category: community.category || '',
    is_public: community.is_public !== false,
    avatar_url: community.avatar_url || '',
    banner_url: community.banner_url || '',
    rules: community.rules || [],
    links: community.links || [],
    // Sprint 1.0c V1.a — items 5 & 6
    join_mode: community.join_mode || 'open',
    welcome_message: community.welcome_message || '',
  });
  const [newRule, setNewRule] = useState('');
  const [newLink, setNewLink] = useState({ title: '', url: '' });
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('general');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const avatarInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  // Brief LOT4.1 — recadrage ajustable avant upload (avatar + bandeau)
  const [cropState, setCropState] = useState(null); // { src, kind: 'avatar'|'banner' }

  const categories = ['Technologie', 'Business', 'Creativite', 'Sante', 'Education', 'Finance', 'Marketing', 'Developpement personnel', 'Sciences', 'Art', 'Musique', 'Sport', 'Voyage', 'Cuisine', 'Autre'];

  const openCrop = (file, kind) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCropState({ src: reader.result, kind });
    reader.readAsDataURL(file);
  };

  const addRule = () => { if (newRule.trim()) { setForm({ ...form, rules: [...form.rules, newRule.trim()] }); setNewRule(''); } };
  const removeRule = (i) => setForm({ ...form, rules: form.rules.filter((_, idx) => idx !== i) });
  const addLink = () => { if (newLink.title.trim() && newLink.url.trim()) { setForm({ ...form, links: [...form.links, { ...newLink }] }); setNewLink({ title: '', url: '' }); } };
  const removeLink = (i) => setForm({ ...form, links: form.links.filter((_, idx) => idx !== i) });

  const handleAvatarUpload = async (file) => {
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/upload/thumbnail`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.thumbnail_url) {
        setForm({ ...form, avatar_url: response.data.thumbnail_url });
      }
    } catch (error) { console.error('Avatar upload error:', error); }
    finally { setUploadingAvatar(false); }
  };

  const handleBannerUpload = async (file) => {
    if (!file) return;
    setUploadingBanner(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/upload`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.file_url) {
        setForm({ ...form, banner_url: response.data.file_url });
      }
    } catch (error) { console.error('Banner upload error:', error); }
    finally { setUploadingBanner(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/communities/${community.community_id}`, form, { withCredentials: true });
      onSave(response.data);
    } catch (error) { console.error(error); }
    finally { setSaving(false); }
  };

  const bannerStyle = form.banner_url
    ? { backgroundImage: `url(${resolveUrl(form.banner_url)})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)' };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="community-edit-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b flex items-center justify-between flex-shrink-0"><h2 className="text-xl font-bold text-gray-900">Gérer la communauté</h2><button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button></div>

        <div className="flex border-b px-6 gap-4 flex-shrink-0">
          {[{ id: 'general', label: 'General' }, { id: 'appearance', label: 'Apparence' }, { id: 'rules', label: 'Regles' }, { id: 'links', label: 'Liens' }].map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)} className={`py-3 text-sm font-medium border-b-2 transition-colors ${activeSection === s.id ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{s.label}</button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeSection === 'general' && (
            <div className="space-y-5">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-community-name" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Decrivez votre communaute..." className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-purple-300 outline-none resize-none text-sm" rows={4} data-testid="edit-community-description" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Categorie</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-purple-300 outline-none text-sm" data-testid="edit-community-category"><option value="">Selectionner</option>{categories.map(c => <option key={c} value={c}>{c}</option>)}</select>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Visibilité</label>
                <p className="text-xs text-gray-500 italic">
                  Toutes les communautés sont visibles dans les listings. Le contrôle d'accès se fait via le mode d'adhésion ci-dessous.
                </p>
              </div>

              {/* Sprint 1.0c V1.a — Mode d'adhésion */}
              <div data-testid="edit-community-join-mode-section">
                <label className="block text-sm font-medium text-gray-700 mb-2">Mode d'adhésion</label>
                <div className="flex gap-3">
                  <button
                    data-testid="edit-join-mode-open"
                    onClick={() => setForm({ ...form, join_mode: 'open' })}
                    className={`flex-1 py-3 rounded-xl border-2 font-medium flex items-center justify-center gap-2 ${form.join_mode === 'open' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-600'}`}
                  >
                    Ouverte (1-clic)
                  </button>
                  <button
                    data-testid="edit-join-mode-invitation"
                    onClick={() => setForm({ ...form, join_mode: 'invitation' })}
                    className={`flex-1 py-3 rounded-xl border-2 font-medium flex items-center justify-center gap-2 ${form.join_mode === 'invitation' ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-600'}`}
                  >
                    Sur invitation
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  En mode "Sur invitation", chaque demande est validée par les admins.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message de bienvenue (optionnel)</label>
                <textarea
                  data-testid="edit-community-welcome-message"
                  value={form.welcome_message}
                  onChange={(e) => setForm({ ...form, welcome_message: e.target.value })}
                  placeholder="Ce message est envoyé en notification aux nouveaux membres."
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-purple-300 outline-none resize-none text-sm"
                  rows={3}
                  maxLength={240}
                />
                <p className="text-xs text-gray-400 mt-1">{form.welcome_message.length}/240</p>
              </div>
            </div>
          )}

          {activeSection === 'appearance' && (
            <div className="space-y-6">
              {/* Banner */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bandeau de couverture</label>
                <div className="relative rounded-xl overflow-hidden h-40 group cursor-pointer" style={bannerStyle} onClick={() => bannerInputRef.current?.click()}>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center text-white">
                      {uploadingBanner ? <Loader2 className="h-8 w-8 animate-spin" /> : <><ImageIcon className="h-8 w-8 mb-1" /><span className="text-sm font-medium">Changer le bandeau</span></>}
                    </div>
                  </div>
                  <input ref={bannerInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { openCrop(e.target.files[0], 'banner'); e.target.value = ''; }} data-testid="banner-upload-input" />
                </div>
                {form.banner_url && (
                  <button onClick={() => setForm({ ...form, banner_url: '' })} className="mt-2 text-xs text-red-500 hover:text-red-600">Supprimer le bandeau</button>
                )}
              </div>

              {/* Avatar */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Image de la communaute</label>
                <div className="flex items-center gap-4">
                  <div className="relative group cursor-pointer" onClick={() => avatarInputRef.current?.click()}>
                    <Avatar className="h-20 w-20 border-4 border-gray-100 shadow-lg">
                      <AvatarImage src={resolveUrl(form.avatar_url)} />
                      <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-xl font-bold">{form.name?.substring(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 rounded-full transition-colors flex items-center justify-center">
                      {uploadingAvatar ? <Loader2 className="h-6 w-6 animate-spin text-white opacity-100" /> : <Camera className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />}
                    </div>
                    <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { openCrop(e.target.files[0], 'avatar'); e.target.value = ''; }} data-testid="avatar-upload-input" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Photo de profil</p>
                    <p className="text-xs text-gray-400">Cliquez pour changer. Format recommande : 200x200px</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'rules' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">Definissez les regles de votre communaute</p>
              {form.rules.length > 0 && (
                <ol className="space-y-2">
                  {form.rules.map((rule, i) => (
                    <li key={i} className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg">
                      <span className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                      <span className="flex-1 text-sm">{rule}</span>
                      <button onClick={() => removeRule(i)} className="text-gray-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                    </li>
                  ))}
                </ol>
              )}
              <div className="flex gap-2">
                <Input value={newRule} onChange={(e) => setNewRule(e.target.value)} placeholder="Ajouter une regle..." onKeyDown={(e) => e.key === 'Enter' && addRule()} data-testid="new-rule-input" className="flex-1" />
                <Button onClick={addRule} disabled={!newRule.trim()} size="sm" variant="outline" data-testid="add-rule-btn"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>
          )}

          {activeSection === 'links' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">Ajoutez des liens utiles</p>
              {form.links.length > 0 && (
                <div className="space-y-2">
                  {form.links.map((link, i) => (
                    <div key={i} className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg">
                      <LinkIcon className="h-4 w-4 text-purple-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{link.title}</p><p className="text-xs text-gray-400 truncate">{link.url}</p></div>
                      <button onClick={() => removeLink(i)} className="text-gray-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="space-y-2">
                <Input value={newLink.title} onChange={(e) => setNewLink({ ...newLink, title: e.target.value })} placeholder="Titre du lien" data-testid="new-link-title" />
                <div className="flex gap-2">
                  <Input value={newLink.url} onChange={(e) => setNewLink({ ...newLink, url: e.target.value })} placeholder="URL (ex: https://...)" className="flex-1" data-testid="new-link-url" />
                  <Button onClick={addLink} disabled={!newLink.title.trim() || !newLink.url.trim()} size="sm" variant="outline" data-testid="add-link-btn"><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t flex gap-3 flex-shrink-0">
          <Button variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
          <Button onClick={handleSave} disabled={saving || !form.name.trim()} className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white" data-testid="save-community-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}Enregistrer</Button>
        </div>
      </div>

      {/* Brief LOT4.1 — recadrage ajustable (avatar rond 1:1, bandeau large) */}
      <ImageCropModal
        isOpen={!!cropState}
        imageSrc={cropState?.src}
        onClose={() => setCropState(null)}
        aspect={cropState?.kind === 'avatar' ? 1 : 3}
        aspectOptions={cropState?.kind === 'banner' ? [
          { label: 'Large 3:1', value: 3 },
          { label: '16:9', value: 16 / 9 },
        ] : undefined}
        title={cropState?.kind === 'avatar' ? "Recadrer l'image de la communauté" : 'Recadrer le bandeau'}
        onCropComplete={(file) => {
          const kind = cropState?.kind;
          setCropState(null);
          if (kind === 'avatar') handleAvatarUpload(file);
          else handleBannerUpload(file);
        }}
      />
    </div>
  );
};

export default EditCommunityModal;
